import {
  PROCESSING_MEDIA_ITEMS,
  LOADING_PROGRESS_DATA,
  AI_VIDEO_USER_PREFERENCES,
  MAX_LOADING_PROGRESS_DATA_LENGTH
} from 'consts'

export const setItemInLocalStorage = <T>(key: string, data: T) => {
  localStorage.setItem(key, JSON.stringify(data))
}

export const getItemFromLocalStorage = (key: string) => {
  return JSON.parse(localStorage.getItem(key) ?? 'null')
}

export const addToLocalStorageProcessingItems = <T extends { id: string }>(
  item: T
) => {
  const processingItems = getItemFromLocalStorage(PROCESSING_MEDIA_ITEMS) || []
  if (
    processingItems &&
    processingItems.find((processingItem: T) => processingItem.id === item.id)
  ) {
    return
  }
  setItemInLocalStorage(PROCESSING_MEDIA_ITEMS, [...processingItems, item])
}

export const removeFromLocalStorageProcessingItems = (
  itemsIds: Array<string>
) => {
  const processingItems = getItemFromLocalStorage(PROCESSING_MEDIA_ITEMS)
  if (!processingItems?.length) return
  setItemInLocalStorage(
    PROCESSING_MEDIA_ITEMS,
    processingItems.filter(
      (item: { id: string }) => !itemsIds.includes(item.id)
    )
  )
}

export interface ILoadingProgressData {
  id: string
  assetId: string | number
  progress: number
  finishTime?: number
  expectedDurationInMilliseconds?: number
  useFakeAcceleration?: boolean
}

export const getLoadingProgressFromLocalStorage = (
  itemId: string | number
): ILoadingProgressData | null => {
  const loadingProgressData = getItemFromLocalStorage(
    LOADING_PROGRESS_DATA
  ) as ILoadingProgressData[]
  if (!loadingProgressData || !loadingProgressData.length) {
    return null
  }

  const loadingProgressDataPerItem = loadingProgressData.find(
    (item: ILoadingProgressData) =>
      item.id === itemId || item.assetId === String(itemId)
  )
  return loadingProgressDataPerItem ?? null
}

export const updateLoadingProgressDataInLocalStorage = ({
  id,
  assetId,
  progress,
  finishTime,
  expectedDurationInMilliseconds,
  useFakeAcceleration
}: Partial<ILoadingProgressData>) => {
  if (!id && assetId === undefined) {
    return
  }
  const _assetId = assetId !== undefined ? String(assetId) : undefined
  const _id = id !== undefined ? String(id) : undefined
  const loadingProgressData =
    getItemFromLocalStorage(LOADING_PROGRESS_DATA) || []

  // normalize search values to strings for consistent comparison
  const searchValues = [_id, _assetId].filter(Boolean) as string[]

  const assignIfDefined = <K extends keyof ILoadingProgressData>(
    obj: Partial<ILoadingProgressData>,
    key: K,
    value: ILoadingProgressData[K] | undefined
  ) => value !== undefined && (obj[key] = value)

  const loadingProgressDataPerItem = loadingProgressData.find(
    (item: ILoadingProgressData) => {
      const itemIdStr = String(item.id)
      const itemAssetIdStr = String(item.assetId)

      return searchValues.some(
        searchValue =>
          itemIdStr === searchValue || itemAssetIdStr === searchValue
      )
    }
  )

  if (loadingProgressDataPerItem) {
    // update existing item with provided values

    const updates: Partial<ILoadingProgressData> = {}
    assignIfDefined(updates, 'assetId', _assetId)
    assignIfDefined(updates, 'progress', progress)
    assignIfDefined(updates, 'finishTime', finishTime)
    assignIfDefined(
      updates,
      'expectedDurationInMilliseconds',
      expectedDurationInMilliseconds
    )
    assignIfDefined(updates, 'useFakeAcceleration', useFakeAcceleration)

    Object.assign(loadingProgressDataPerItem, updates)
  } else {
    const newItem: Partial<ILoadingProgressData> = {
      id,
      progress: progress ?? 0
    }

    assignIfDefined(newItem, 'assetId', _assetId)
    assignIfDefined(newItem, 'finishTime', finishTime)
    assignIfDefined(
      newItem,
      'expectedDurationInMilliseconds',
      expectedDurationInMilliseconds
    )
    assignIfDefined(newItem, 'useFakeAcceleration', useFakeAcceleration)

    loadingProgressData.push(newItem)
  }

  setItemInLocalStorage(LOADING_PROGRESS_DATA, loadingProgressData)
}

export const removeLoadingProgressFromLocalStorage = (itemId: string) => {
  const loadingProgressData = getItemFromLocalStorage(LOADING_PROGRESS_DATA)
  if (!loadingProgressData?.length) return
  setItemInLocalStorage(
    LOADING_PROGRESS_DATA,
    loadingProgressData.filter(
      (item: ILoadingProgressData) =>
        item.id !== itemId && item.assetId !== String(itemId)
    )
  )
}

export const removeOldLoadingProgressDataFromLocalStorage = () => {
  const loadingProgressData = getItemFromLocalStorage(LOADING_PROGRESS_DATA)
  if (!loadingProgressData?.length) return
  if (loadingProgressData.length > MAX_LOADING_PROGRESS_DATA_LENGTH) {
    setItemInLocalStorage(
      LOADING_PROGRESS_DATA,
      loadingProgressData.slice(-MAX_LOADING_PROGRESS_DATA_LENGTH)
    )
  }
}

export interface IAIVideoUserPreferences {
  quality?: number
  aspectRatio?: string
}

export const saveAIVideoUserPreferences = (
  preferences: IAIVideoUserPreferences
) => {
  const existing = getAIVideoUserPreferences()
  const updated = { ...existing, ...preferences }
  sessionStorage.setItem(AI_VIDEO_USER_PREFERENCES, JSON.stringify(updated))
}

export const getAIVideoUserPreferences = (): IAIVideoUserPreferences => {
  const stored = sessionStorage.getItem(AI_VIDEO_USER_PREFERENCES)
  return stored ? JSON.parse(stored) : {}
}
